<template>
  <div class="main-content">

    <div class="details-container">
      <div class="section-header">
        <h2>Course Modules Details</h2>
      </div>
      <div class="details-header">
        <input
          class="search-input"
          type="text"
          placeholder="Search module data"
          v-model="moduleSearchQuery"
          @input="debouncedModuleSearch"
        />
        <div class="export-csv-container">
          <button class="btn" @click="downloadModuleData()">{{ moduleDownloadButtonText }}</button>
        </div>
      </div>
      <div ref="courseModuleTable" id="course-module-table"></div>
    </div>

    <hr />

    <div class="summary-container">
      <h2>Module Summary</h2>

      <div class="selection-container">
        <select id="moduleselect" class="custom-select" v-if="(this.modules.length > 1)" v-model="selected_module"  @change="selectOption()">
          <option disabled value="">Select a module</option>
          <option v-for="module in modules" :key="module.id">{{ module.attributes.name }}</option>
        </select>
      </div>

      <div class="summary">
        <p>The report below shows assessment information for learners enrolled in this section. Assessments refer to the feedback that learners receive on the debrief of interaction learning objects. </p>
        <p><span>Total learners in this section: </span>{{ total_learners }}</p>
        <p><span>Reattempts enabled: </span>{{ reattempts_enabled ? "Yes" : "No" }}</p>
        <div v-if="this.modules.length === 1">
          <p><span>Module: </span>{{ modules[0].attributes.name }}</p>
        </div>
      </div>
    </div>
    <div class="details-container">
      <h2>Learner Details</h2>
      <div class="details-header">
        <input
          class="search-input"
          type="text"
          placeholder="Search learners"
          v-model="detailSearchQuery"
          @input="debouncedDetailSearch"
        />
        <div class="export-csv-container">
          <button class="btn" @click="downloadTableData()">{{ downloadButtonText }}</button>
        </div>
      </div>
      <div ref="assessmentTable" id="main-data-table"></div>
    </div>

    <!-- Grade Editing Modal -->
    <GradeEditModal />

  </div>
</template>
<script>
import { utilFunctionService } from "@/utils/utils.service";
import { Tabulator, FilterModule, SortModule, DownloadModule, ExportModule, PageModule, AjaxModule, EditModule, ValidateModule, InteractionModule, FrozenColumnsModule, FormatModule } from 'tabulator-tables';
import XLSX from "xlsx";
import _ from 'lodash';
import GradeEditModal from '@/components/modals/gradeEditModal.vue';
import api from '@/services/api';
window.XLSX = XLSX;
Tabulator.registerModule([FilterModule, SortModule, DownloadModule, ExportModule, PageModule, AjaxModule, EditModule, ValidateModule, InteractionModule, FrozenColumnsModule, FormatModule]);

export default {
  name: "Assessment-Reporting",
  components: {
    GradeEditModal
  },
  props: ["section_data"],
  data() {
    return {
      // Common data
      section: null,
      moduleSearchQuery: "",
      detailSearchQuery: "",
      isLoading: false,
      
      // Module selection data
      modules: [],
      selected_module: '',
      
      // Shared metadata
      total_learners: '',
      total_learning_objs: '',
      reattempts_disabled: false,
      reattempts_enabled: true,
      
      // Main detailed table data
      lo_table_headers: [],
      detailColumns: [],
      detailTabulator: null,
      detailCurrentPage: 1,
      detailTotalPages: 1,
      detailTotalRecords: 0,
      
      // Course module summary table data
      moduleTabulator: null,
      moduleCurrentPage: 1,
      moduleTotalPages: 1,
      moduleTotalRecords: 0,
      
      // Common settings
      pageSize: 20
    };
  },
  
  created() {
    this.section = JSON.parse(localStorage.getItem("section"));
    utilFunctionService.showLoader();
    this.debouncedModuleSearch = _.debounce(() => {
      this.reloadModuleTable();
    }, 300);
    this.debouncedDetailSearch = _.debounce(() => {
      this.reloadDetailTable();
    }, 300);
    
    this.modules = this.section.attributes.learn_mods || [];
    if (this.modules.length > 0) {
      this.selected_module = this.modules[0].attributes.name;
      this.initializeData();
    } else {
      console.error("No modules found in section data");
      utilFunctionService.hideLoader();
    }
  },
  
  mounted() {
  },
  
  computed: {
    downloadButtonText() {
      return this.detailSearchQuery && this.detailSearchQuery.trim() !== ''
        ? `Download Searched Learner Data`
        : `Download All Learner Data`;
    },
    moduleDownloadButtonText() {
      return this.moduleSearchQuery && this.moduleSearchQuery.trim() !== ''
        ? `Download Searched Module Data`
        : `Download All Module Data`;
    },
    detailApiUrl() {
      return this.section
        ? `${process.env.VUE_APP_URL}instructor/v1/sections/${this.section.id}/reports_assessment/learner_assessment_data`
        : null;
    },
    moduleApiUrl() {
      return this.section
        ? `${process.env.VUE_APP_URL}instructor/v1/sections/${this.section.id}/reports_assessment/course_module_summary`
        : null;
    }
  },
  methods: {
    initializeData() {
      this.setupColumns();
      this.$nextTick(() => {
        this.initializeDetailTabulator();
        this.initializeModuleTabulator();
      });
      
      utilFunctionService.hideLoader();
    },
    setupColumns() {
      // Setup columns for the detail tabulator
      this.detailColumns = [
        {title: "Name", field: "learner_name", frozen: true},
        {title: "Email", field: "learner_email", frozen: true},
        {title: "Total Points", field: "total_score_module"},
      ];
      
      // Add learning object columns based on table headers
      if (this.lo_table_headers && this.lo_table_headers.length > 0) {
        for (let i = 0; i < this.total_learning_objs; i++) {
          const learning_object_name = this.lo_table_headers[i].lo_name;
          const lo_skills = this.lo_table_headers[i].lo_skills;

          // Add learning object score column
          const ulo_score_field = "total_score_ulo_" + i + "_key";
          const lo_score_object = {
            title: learning_object_name,
            field: ulo_score_field,
            width: '15%',
          };
          this.detailColumns.push(lo_score_object);

          // Create skill columns array
          const skill_names_array = [];
          for (let k = 0; k < parseInt(lo_skills.total_skills); k++) {
            const skill_assessment_field = "ulo_" + i + "_skill_"+ k + "_assessment";
            const skill_names_object = {
              title: lo_skills['skill_name_' + k],
              field: skill_assessment_field,
              width: '15%',
              editable: false, // Disable default editing to use cellClick
              cellClick: (e, cell) => {
                this.showGradeEditModal(cell, i, k);
              },
              validator: ["min:0", "max:100", "numeric"],
              formatter: (cell) => {
                const rowData = cell.getRow().getData();
                const isEditable = rowData[`ulo_${i}_skill_${k}_is_editable`];

                if (isEditable) {
                  cell.getElement().classList.add('editable-cell');
                }

                return cell.getValue();
              }
            };
            skill_names_array.push(skill_names_object);
          }
          
          // Add learning object column group with skills
          const new_data = {
            title: learning_object_name, 
            columns: skill_names_array
          };
          this.detailColumns.push(new_data);
        }
      }
      
    },
    
    getAjaxConfig() {
      // Shared AJAX configuration for both tables
      return {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: utilFunctionService.getAuthToken(),
          "Cache-Control": "no-cache, no-store",
          "Pragma": "no-cache"
        }
      };
    },
    
    getRequestParams(page, tableType = 'detail') {
      // Request parameters for specific table type
      const searchQuery = tableType === 'module' ? this.moduleSearchQuery : this.detailSearchQuery;
      return {
        page: page || 1,
        search: searchQuery || '',
        learn_mod_id: this.getSelectedModuleId() || '',
        _t: Date.now()
      };
    },
    
    processMetadata(response) {
      if (!response.data || !response.data.attributes) return;
      
      const attributes = response.data.attributes;

      if (attributes.lo_table_headers) {
        this.lo_table_headers = attributes.lo_table_headers;
      }
      
      if (attributes.total_learning_objs !== undefined) {
        this.total_learning_objs = attributes.total_learning_objs;
      }
      
      if (attributes.total_learners_count !== undefined) {
        this.total_learners = attributes.total_learners_count;
      }
      
      if (attributes.reattempts_disabled !== undefined) {
        this.reattempts_disabled = !!attributes.reattempts_disabled;
        this.reattempts_enabled = !this.reattempts_disabled;
      }
    },
    
    initializeDetailTabulator() {
      if (!this.$refs.assessmentTable || !this.detailApiUrl) {
        return;
      }
      
      
      const tabulatorOptions = {
        pagination: true,
        paginationMode: "remote",
        paginationSize: this.pageSize,
        paginationSizeSelector: [5, 10, 20, 50],
        paginationInitialPage: 1,
        ajaxURL: this.detailApiUrl,
        ajaxConfig: this.getAjaxConfig(),
        ajaxParams: () => this.getRequestParams(this.detailCurrentPage, 'detail'),
        ajaxResponse: (url, params, response) => {
          
          if (!response.data || !response.data.attributes) {
            return { data: [], last_page: 1, total_records: 0 };
          }
          
          // Extract metadata
          this.processMetadata(response);
          
          // Update columns if needed
          this.setupColumns();
          if (this.detailTabulator) {
            this.detailTabulator.setColumns(this.detailColumns);
          }
          
          // Update pagination info
          this.detailCurrentPage = response.meta?.current_page || 1;
          this.detailTotalPages = response.meta?.total_pages || response.last_page || 1;
          this.detailTotalRecords = response.meta?.total_records || 0;
          
          return {
            data: response.data.attributes.section_learners || [],
            last_page: this.detailTotalPages,
            total_records: this.detailTotalRecords
          };
        },
        columns: this.detailColumns,
        layout: "fitData",
        layoutColumnsOnNewData: true,
        resizableColumns: true,
        height: "600px",
        selectable: true,
        persistence: {
          filter: true, 
          columns: true,
        },
        sortable: true,
        downloadConfig: {
          columnHeaders: true, 
          columnGroups: true, 
          rowGroups: false, 
          columnCalcs: false, 
          dataTree: true
        },
        dataLoaded: () => {
          this.isLoading = false;
        },
        pageLoaded: (pageno) => {
          this.detailCurrentPage = pageno;
        }
      };
      
      // Create the Tabulator instance
      this.detailTabulator = new Tabulator(this.$refs.assessmentTable, tabulatorOptions);
      
      // Add event listeners
      this.detailTabulator.on("tableBuilt", () => {
      });
      
      this.detailTabulator.on("error", (error) => {
        console.error("Detail Tabulator error:", error);
        this.isLoading = false;
      });
      
      // Add grade editing event handlers
      this.detailTabulator.on("cellEdited", () => {
        // This will be triggered if direct editing is enabled on any cell
      });
      
      this.detailTabulator.on("validationFailed", (cell, value, validators) => {
        let errorMsg = "Invalid value";
        
        validators.forEach(validator => {
          if (validator.type === "min") {
            errorMsg = `Value must be at least ${validator.parameters}`;
          } else if (validator.type === "max") {
            errorMsg = `Value cannot exceed ${validator.parameters}`;
          } else if (validator.type === "numeric") {
            errorMsg = "Value must be a number";
          }
        });
        
        utilFunctionService.showerr(errorMsg);
      });
    },
    
    initializeModuleTabulator() {
      if (!this.$refs.courseModuleTable || !this.moduleApiUrl) {
        return;
      }
      
      
      const tabulatorOptions = {
        pagination: true,
        paginationMode: "remote",
        paginationSize: this.pageSize,
        paginationSizeSelector: [5, 10, 20, 50],
        paginationInitialPage: 1,
        ajaxURL: this.moduleApiUrl,
        ajaxConfig: this.getAjaxConfig(),
        ajaxParams: () => this.getRequestParams(this.moduleCurrentPage, 'module'),
        ajaxResponse: (url, params, response) => {
          
          if (!response.data || !response.data.attributes) {
            return { data: [], last_page: 1, total_records: 0 };
          }
          
          // Extract metadata if not already processed by detail table
          if (!this.total_learners) {
            this.processMetadata(response);
          }
          
          // Update pagination info
          this.moduleCurrentPage = response.meta?.current_page || 1;
          this.moduleTotalPages = response.meta?.total_pages || response.last_page || 1;
          this.moduleTotalRecords = response.meta?.total_records || 0;
          
          return {
            data: response.data.attributes.course_module_grading_details || [],
            last_page: this.moduleTotalPages,
            total_records: this.moduleTotalRecords
          };
        },
        autoColumns: true,
        columnHeaderVertAlign: "bottom",
        layout: "fitData",
        layoutColumnsOnNewData: true,
        resizableColumns: true,
        height: "600px",
        sortable: true,
        downloadConfig: {
          columnHeaders: true, 
          columnGroups: true, 
          rowGroups: false, 
          columnCalcs: false, 
          dataTree: true
        },
        dataLoaded: () => {
          this.isLoading = false;
        },
        pageLoaded: (pageno) => {
          this.moduleCurrentPage = pageno;
        }
      };
      
      // Create the Tabulator instance
      this.moduleTabulator = new Tabulator(this.$refs.courseModuleTable, tabulatorOptions);
      
      // Add event listeners
      this.moduleTabulator.on("tableBuilt", () => {
      });
      
      this.moduleTabulator.on("error", (error) => {
        console.error("Module Tabulator error:", error);
        this.isLoading = false;
      });
    },
    
    reloadModuleTable() {
      this.isLoading = true;

      // Reset to page 1 when searching
      this.moduleCurrentPage = 1;

      // Use replaceData() to reload with updated ajaxParams function
      if (this.moduleTabulator) {
        this.moduleTabulator.replaceData();
      }
    },

    reloadDetailTable() {
      this.isLoading = true;

      // Reset to page 1 when searching
      this.detailCurrentPage = 1;

      // Use replaceData() to reload with updated ajaxParams function
      if (this.detailTabulator) {
        this.detailTabulator.replaceData();
      }
    },

    reloadBothTables() {
      // Called when module selection changes
      this.reloadModuleTable();
      this.reloadDetailTable();
    },
    
    selectOption() {
      // v-model has already updated selected_module
      // Use $nextTick to ensure Vue has finished updating
      this.$nextTick(() => {
        // Only reload the Learner Details table when module changes
        this.reloadDetailTable();
      });
    },
    
    getSelectedModuleId() {
      if (!this.selected_module) return null;
      
      const mod = this.modules.find(elem => elem.attributes.name === this.selected_module);
      return mod ? mod.id : null;
    },
    
    downloadTableData() {
      if (!this.detailTabulator) {
        console.error("Tabulator not initialized");
        return;
      }
      
      const spreadsheetName = this.section.attributes.name + " Grading Report Export.xlsx";
      
      // If there's a search query or we want all data, use our custom endpoint
      if (this.detailSearchQuery && this.detailSearchQuery.trim() !== '') {
        // Show loading indicator
        utilFunctionService.showLoader();
        
        // Use custom endpoint to get all data for the search
        fetch(`${process.env.VUE_APP_URL}instructor/v1/sections/${this.section.id}/reports_assessment/export_all_data?search=${encodeURIComponent(this.detailSearchQuery)}&current_module=${encodeURIComponent(this.selected_module)}`, {
          method: 'GET',
          headers: {
            'Authorization': utilFunctionService.getAuthToken(),
            'Content-Type': 'application/json'
          }
        })
        .then(response => response.blob())
        .then(blob => {
          // Create a download link and trigger download
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = spreadsheetName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          utilFunctionService.hideLoader();
        })
        .catch(error => {
          console.error("Error downloading data:", error);
          utilFunctionService.hideLoader();
          // Fallback to regular download if the custom endpoint fails
          this.detailTabulator.download("xlsx", spreadsheetName);
        });
      } else {
        // For downloading all data without search filtering
        utilFunctionService.showLoader();
        
        // Use the same endpoint but without search parameter
        fetch(`${process.env.VUE_APP_URL}instructor/v1/sections/${this.section.id}/reports_assessment/export_all_data?current_module=${encodeURIComponent(this.selected_module)}`, {
          method: 'GET',
          headers: {
            'Authorization': utilFunctionService.getAuthToken(),
            'Content-Type': 'application/json'
          }
        })
        .then(response => response.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = spreadsheetName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          utilFunctionService.hideLoader();
        })
        .catch(error => {
          console.error("Error downloading all data:", error);
          utilFunctionService.hideLoader();
          // Fallback to regular tabulator download
          this.detailTabulator.download("xlsx", spreadsheetName);
        });
      }
    },
    downloadModuleData() {
      if (!this.moduleTabulator) {
        console.error("Module Tabulator not initialized");
        return;
      }

      const spreadsheetName = this.section.attributes.name + " Course Module Summary Export.xlsx";

      // If there's a search query, use our custom endpoint
      if (this.moduleSearchQuery && this.moduleSearchQuery.trim() !== '') {
        // Show loading indicator
        utilFunctionService.showLoader();

        // Use custom endpoint to get all data for the search
        fetch(`${process.env.VUE_APP_URL}instructor/v1/sections/${this.section.id}/reports_assessment/export_course_module_summary?search=${encodeURIComponent(this.moduleSearchQuery)}&current_module=${encodeURIComponent(this.selected_module)}`, {
          method: 'GET',
          headers: {
            'Authorization': utilFunctionService.getAuthToken(),
            'Content-Type': 'application/json'
          }
        })
        .then(response => response.blob())
        .then(blob => {
          // Create a download link and trigger download
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = spreadsheetName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          utilFunctionService.hideLoader();
        })
        .catch(error => {
          console.error("Error downloading module data:", error);
          utilFunctionService.hideLoader();
          // Fallback to regular download if the custom endpoint fails
          this.moduleTabulator.download("xlsx", spreadsheetName);
        });
      } else {
        // For downloading all data without search filtering
        utilFunctionService.showLoader();

        // Use the same endpoint but without search parameter
        fetch(`${process.env.VUE_APP_URL}instructor/v1/sections/${this.section.id}/reports_assessment/export_course_module_summary?current_module=${encodeURIComponent(this.selected_module)}`, {
          method: 'GET',
          headers: {
            'Authorization': utilFunctionService.getAuthToken(),
            'Content-Type': 'application/json'
          }
        })
        .then(response => response.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = spreadsheetName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          utilFunctionService.hideLoader();
        })
        .catch(error => {
          console.error("Error downloading all module data:", error);
          utilFunctionService.hideLoader();
          // Fallback to regular tabulator download
          this.moduleTabulator.download("xlsx", spreadsheetName);
        });
      }
    },

    showGradeEditModal(cell, uloIndex, skillIndex) {
      const rowData = cell.getRow().getData();
      const columnDef = cell.getColumn().getDefinition();

      // Safety check: Ensure this skill is editable
      const isEditableField = `ulo_${uloIndex}_skill_${skillIndex}_is_editable`;
      const isEditable = rowData[isEditableField];

      if (!isEditable) {
        console.warn('Attempted to edit non-editable skill:', {
          uloIndex,
          skillIndex,
          skillName: columnDef.title
        });
        utilFunctionService.showerr('This assessment cannot be edited. Only point-based skills can be modified.');
        return;
      }

      // Get the actual numeric earned points instead of the formatted display value
      const earnedPointsField = `ulo_${uloIndex}_skill_${skillIndex}_earned_points`;
      const currentValue = rowData[earnedPointsField] || 0;

      // Get max points from row data using correct field pattern
      const maxPointsField = `ulo_${uloIndex}_skill_${skillIndex}_max_points`;
      const skillMaxPoints = rowData[maxPointsField] || 100;

      this.$modal.show('gradeEditModal', {
        learnerName: rowData.learner_name || 'Unknown Student',
        skillName: columnDef.title || 'Unknown Skill',
        currentValue: currentValue,
        maxPoints: skillMaxPoints,
        userSectionId: rowData.user_section_id,
        uloAssmntSkillId: this.extractUloAssmntSkillId(rowData, uloIndex, skillIndex),
        onSave: async (gradeData) => {
          await this.updateGrade(cell, gradeData);
        }
      });
    },
    

    extractUloAssmntSkillId(rowData, uloIndex, skillIndex) {
      // Use the correct field pattern: ulo_X_skill_Y_id
      const skillIdField = `ulo_${uloIndex}_skill_${skillIndex}_id`;
      const skillTypeField = `ulo_${uloIndex}_skill_type`;

      if (rowData[skillIdField]) {
        return {
          id: rowData[skillIdField],
          type: rowData[skillTypeField] || 'ulo_assmnt_skill'
        };
      }

      if (process.env.NODE_ENV !== 'production') {
        console.warn(`Could not find skill ID field: ${skillIdField}. Available skill fields:`, Object.keys(rowData).filter(k => k.includes('skill')));
      }
      return null;
    },
    
    async updateGrade(cell, gradeData) {
      const skillData = gradeData.uloAssmntSkillId;

      if (!skillData || !skillData.id) {
        utilFunctionService.showerr('Unable to identify assessment skill. Please refresh and try again.');
        return;
      }

      try {
        utilFunctionService.showLoader();

        const payload = {
          skill_type: skillData.type,
          ulo_assmnt_skill: {
            earned_points: gradeData.newValue,
            assessment_label: gradeData.reason
          }
        };

        const response = await api.updateUloAssmtnSkillGrade(
          skillData.id,
          payload
        );
        
        if (response.status === 200 || response.status === 204) {
          // Update cell value on success
          cell.setValue(gradeData.newValue);
          utilFunctionService.showSuccess('Grade updated successfully');
          
          // Reload both tables so Course Modules Details reflects the updated points
          this.reloadDetailTable();
          this.reloadModuleTable();
        }
        
      } catch (error) {
        console.error('Grade update failed:', error);
        
        let errorMessage = 'Failed to update grade';
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response?.data?.errors) {
          errorMessage = Array.isArray(error.response.data.errors) 
            ? error.response.data.errors.join(', ')
            : error.response.data.errors;
        }
        
        utilFunctionService.showerr(errorMessage);
        throw error; // Re-throw to let modal handle the error state
        
      } finally {
        utilFunctionService.hideLoader();
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.main-content {
  padding: 0;
  width: 100%;
}
.completion-grid {
  width: 100%;
  height: 500px;
  margin-top: 32px;
}
.selection-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  .custom-select {
    width: 35%;
  }
}
.summary-container {
  margin-bottom: 64px;
  h2 {
    font-family: 'robotobold', sans-serif;
    font-size: 18px;
    text-transform: capitalize;
    margin-bottom: 16px;
  }
  .summary {
    margin: 32px 0 0 32px;
    p {
      margin-bottom: 16px;
      span {
        font-weight: bold;
      }
    }
    .summary-learners {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
  }
}

.aggregate-container {
  padding-bottom: 3em;
}
.details-container, .aggregate-container {
  h2 {
    font-family: 'robotobold', sans-serif;
    font-size: 18px;
    text-transform: capitalize;
  }
  h3 {
    font-size: 16px;
    font-family: 'robotobold', sans-serif;
    text-transform: capitalize;
  }
  .details-header {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: flex-start;
    .search-input {
      color: #4a5568;
      padding: 0.5rem 0.5rem;
      border-width: 1px;
      border-radius: 0.25rem;
      background-color: #fff;
      margin-left: 16px;
    }
  }
  .export-csv-container {
    display: flex;
    flex-direction: column;
    margin-left: 16px;
  }
  .datePickerBtn {
    color: #3182ce;
    padding: 0.5rem;
    border-width: 1px;
    border-radius: 0.25rem;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-color: #bee3f8;
    background-color: #ebf8ff;
    svg {
      fill: currentColor;
      width: 1rem;
      height: 1rem;
    }
  }
  .datePickerInput {
    color: #4a5568;
    padding: 0.5rem 0.5rem;
    border-width: 1px;
    border-radius: 0.25rem;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    background-color: #fff;
  }
}
  .export-csv-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    .btn {
      border: 3px solid #3dbc9e;
      background: #fff;
    }
    .settings-btn {
      margin-top: 16px;
      border: none;
      font-weight: bold;
      background: none;
    }
  }

  .tabulator-table {
    .tabulator-row {
      .tabulator-cell {
        white-space: pre-wrap;
        word-break: break-word;
        height: 100px !important;
      }
    }
  }
  .tabulator {
    margin-top: 16px;
    background: #fff;
    white-space: normal !important;
    .tabulator-col-title-holder {
    .tabulator-col-title {
      white-space: normal !important;
    }
  }  
  }

/* New styles for toggleable section */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 10px 5px;
  margin-bottom: 10px;
  border-radius: 4px;
  background-color: #f9f9f9;
  transition: background-color 0.2s;
}

.section-header:hover {
  background-color: #eaeaea;
}

/* Grade editing styles */
::v-deep .editable-cell {
  background-color: #f0f9ff !important;
  cursor: pointer !important;
  position: relative;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e0f2fe !important;
    box-shadow: inset 0 0 0 2px #0284c7;
    transform: scale(1.02);
  }

  &::after {
    content: "✏️";
    position: absolute;
    top: 2px;
    right: 4px;
    font-size: 12px;
    opacity: 0.6;
    pointer-events: none;
  }

  &:hover::after {
    opacity: 0.9;
  }
}


::v-deep .tabulator-validation-fail {
  background-color: #fef2f2 !important;
  border: 1px solid #ef4444 !important;
}

::v-deep .tabulator-cell-editing {
  background-color: #fef3c7 !important;
  border: 2px solid #f59e0b !important;
}

</style>